import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, TrendingUp, Repeat, Lock, ArrowRight, Copy, RotateCw } from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import { Nav, Footer } from "@/components/Chrome";
import { NeuralField } from "@/components/NeuralField";
import { PersonalizedIntelligence } from "@/components/PersonalizedIntelligence";
import { Milestones } from "@/components/Milestones";
import { scoreColor, scoreBand } from "@/components/Gauge";
import { getHistory } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

export default function History() {
  const { user } = useAuth();
  const [audits, setAudits] = useState(null);

  useEffect(() => {
    if (!user) return;
    getHistory().then(setAudits).catch(() => setAudits([]));
  }, [user]);

  const avg =
    audits && audits.length
      ? Math.round(audits.reduce((s, a) => s + a.score, 0) / audits.length)
      : 0;

  const totalLoops =
    audits && audits.length
      ? audits.reduce((s, a) => s + (a.est_loops_saved || 0), 0)
      : 0;

  const fmt = (iso) => {
    const d = new Date(iso);
    return (
      d.toLocaleDateString(undefined, { month: "short", day: "numeric" }) +
      " " +
      d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })
    );
  };

  const chartData =
    audits && audits.length
      ? [...audits].reverse().map((a, i) => ({
          n: i + 1,
          score: a.score,
          label: fmt(a.created_at),
        }))
      : [];

  const ChartTip = ({ active, payload }) => {
    if (!active || !payload || !payload.length) return null;
    const d = payload[0].payload;
    return (
      <div className="glass rounded-lg px-3 py-2 font-mono text-xs">
        <p className="text-white">Audit #{d.n}</p>
        <p style={{ color: scoreColor(d.score) }}>Score {d.score}</p>
        <p className="text-[var(--muted)]">{d.label}</p>
      </div>
    );
  };

  return (
    <div className="relative text-[var(--text)] min-h-screen">
      <Nav />
      <div className="absolute top-0 inset-x-0 h-[360px] overflow-hidden pointer-events-none">
        <NeuralField className="w-full h-full opacity-70" density={55} />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-[#05060a]" />
      </div>

      <main className="relative max-w-7xl mx-auto px-6 md:px-10 pt-28 pb-8">
        <p className="font-mono text-xs tracking-[0.3em] text-neon-cyan mb-3">// SESSION LOG</p>
        <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight leading-none text-white">
          My prompt history
        </h1>
        <p className="font-mono text-sm text-[var(--muted)] mt-3 max-w-lg">
          Every audit you've run, newest first. Watch your average climb as your
          prompts get sharper.
        </p>

        <div className="mt-10">
          {user === undefined ? (
            <div className="flex items-center gap-3 font-mono text-sm text-neon-cyan py-20">
              <Loader2 className="animate-spin" size={18} /> Loading...
            </div>
          ) : !user ? (
            <div className="glass neon-border rounded-2xl p-10 md:p-12 mt-4 text-center max-w-2xl mx-auto" data-testid="history-auth-gate">
              <span className="inline-flex items-center justify-center w-14 h-14 rounded-2xl border border-neon-cyan/50 glow-cyan mb-6">
                <Lock size={26} className="text-neon-cyan" />
              </span>
              <h2 className="font-display text-3xl font-bold tracking-tight text-white leading-tight">
                Create an account to unlock your progress.
              </h2>
              <p className="font-mono text-sm text-[var(--muted)] mt-4 leading-relaxed max-w-md mx-auto">
                Auditing is free. But you'll never learn from your mistakes without an
                account — Coretext tracks every build, charts your readiness over time,
                and estimates the usage you avoid so you watch yourself get sharper at
                vibecoding prompts.
              </p>
              <Link
                to="/login"
                data-testid="history-gate-cta"
                style={{ backgroundColor: "#22d3ee", color: "#05060a" }}
                className="mt-7 inline-flex items-center gap-2 rounded-lg font-display font-bold tracking-wide py-3 px-7 hover:glow-cyan hover:-translate-y-0.5 transition-transform transition-shadow duration-200"
              >
                CREATE FREE ACCOUNT <ArrowRight size={18} strokeWidth={2.5} />
              </Link>
              <p className="font-mono text-xs text-[var(--muted)] mt-4">
                Already have one?{" "}
                <Link to="/login" className="text-neon-cyan hover:underline">Sign in</Link>
              </p>
            </div>
          ) : (
            <AuthedHistory audits={audits} avg={avg} totalLoops={totalLoops} chartData={chartData} ChartTip={ChartTip} fmt={fmt} />
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}

function HistoryRowActions({ audit }) {
  const navigate = useNavigate();
  const copy = async (text, label) => {
    if (!text) { toast.error(`No ${label} to copy.`); return; }
    try { await navigator.clipboard.writeText(text); toast.success(`${label} copied.`); }
    catch { toast.error("Copy failed."); }
  };
  const rerun = () => {
    if (!audit.original_prompt) { toast.error("Original prompt wasn't stored for this audit."); return; }
    navigate("/", { state: { prefillPrompt: audit.original_prompt, prefillPlatform: audit.platform } });
  };
  return (
    <div className="flex items-center gap-2.5">
      <Link to={`/audit/${audit.id}`} className="text-neon-cyan hover:underline" data-testid={`history-view-${audit.id}`}>view</Link>
      <button onClick={() => copy(audit.original_prompt, "Original prompt")} title="Copy original prompt"
        className="text-[var(--muted)] hover:text-white transition-colors" data-testid={`history-copy-original-${audit.id}`}>
        <Copy size={14} />
      </button>
      {audit.improved_prompt && (
        <button onClick={() => copy(audit.improved_prompt, "Improved prompt")} title="Copy improved prompt"
          className="text-[var(--muted)] hover:text-neon-cyan transition-colors" data-testid={`history-copy-improved-${audit.id}`}>
          <Copy size={14} className="text-neon-cyan/70" />
        </button>
      )}
      <button onClick={rerun} title="Re-run audit (prefills original prompt)"
        className="text-[var(--muted)] hover:text-neon-green transition-colors inline-flex items-center gap-1" data-testid={`history-rerun-${audit.id}`}>
        <RotateCw size={14} /> <span className="text-[11px]">re-run</span>
      </button>
    </div>
  );
}

function AuthedHistory({ audits, avg, totalLoops, chartData, ChartTip, fmt }) {
  return (
    <>
      <Milestones audits={audits} />
      <div className="mb-2">
        <PersonalizedIntelligence />
        <div className="glass rounded-2xl p-5 flex items-center justify-between gap-3 flex-wrap" data-testid="history-connectors-link">
          <p className="font-mono text-sm text-[var(--muted)]">Connect your build tools to make CoreText smarter about how you build.</p>
          <Link to="/intelligence" className="font-mono text-sm text-neon-cyan hover:underline inline-flex items-center gap-1.5">Manage connectors <ArrowRight size={14} /></Link>
        </div>
      </div>

        {audits === null ? (
          <div className="flex items-center gap-3 font-mono text-sm text-neon-cyan py-20">
            <Loader2 className="animate-spin" size={18} /> Loading history...
          </div>
        ) : audits.length === 0 ? (
          <div className="glass neon-border rounded-2xl p-12 mt-10 text-center">
            <p className="font-display text-2xl font-bold text-white">No audits yet.</p>
            <p className="font-mono text-sm text-[var(--muted)] mt-2">
              Run your first audit to start tracking your score.
            </p>
            <Link
              to="/"
              data-testid="history-empty-cta"
              style={{ backgroundColor: "#22d3ee", color: "#05060a" }}
              className="mt-6 inline-flex rounded-lg font-display font-bold tracking-wide py-3 px-6 hover:glow-cyan hover:-translate-y-0.5 transition-transform transition-shadow duration-200"
            >
              AUDIT A PROMPT →
            </Link>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
              {[
                { label: "TOTAL AUDITS", value: audits.length, color: "#E7ECF3" },
                { label: "AVERAGE SCORE", value: avg, color: scoreColor(avg) },
                { label: "BEST SCORE", value: Math.max(...audits.map((a) => a.score)), color: "#22d3ee", icon: true },
                { label: "REBUILD LOOPS AVOIDED", value: "~" + totalLoops, color: "#22e58a", saved: true },
              ].map((s, i) => (
                <motion.div key={s.label}
                  initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }}
                  className={`glass rounded-2xl p-5 ${s.saved ? "neon-border" : ""}`}
                  style={s.saved ? { border: "1px solid rgba(34,229,138,0.4)", boxShadow: "0 0 24px rgba(34,229,138,0.12)" } : {}}>
                  <p className="font-mono text-[11px] tracking-[0.25em] text-[var(--muted)] flex items-center gap-1.5">
                    {s.icon && <TrendingUp size={13} className="text-neon-cyan" />}
                    {s.saved && <Repeat size={13} className="text-neon-green" />}
                    {s.label}
                  </p>
                  <p className="font-display text-4xl font-bold tracking-tight mt-1"
                    style={{ color: s.color, textShadow: `0 0 18px ${s.color}55` }}>
                    {s.value}
                  </p>
                </motion.div>
              ))}
            </div>

            {chartData.length > 1 && (
              <motion.div
                initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
                className="glass rounded-2xl mt-6 p-6" data-testid="trend-chart">
                <p className="font-mono text-[11px] tracking-[0.25em] text-[var(--muted)] mb-4">
                  READINESS OVER TIME
                </p>
                <div style={{ width: "100%", height: 220 }}>
                  <ResponsiveContainer>
                    <AreaChart data={chartData} margin={{ top: 8, right: 12, left: -6, bottom: 0 }}>
                      <defs>
                        <linearGradient id="scoreFill" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#22d3ee" stopOpacity={0.4} />
                          <stop offset="100%" stopColor="#22d3ee" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid stroke="rgba(120,200,230,0.08)" vertical={false} />
                      <XAxis dataKey="n" stroke="#7A8699" tick={{ fontFamily: "JetBrains Mono", fontSize: 11 }} tickLine={false} axisLine={{ stroke: "rgba(120,200,230,0.12)" }} />
                      <YAxis domain={[0, 100]} ticks={[0, 25, 50, 75, 100]} stroke="#7A8699" tick={{ fontFamily: "JetBrains Mono", fontSize: 11 }} tickLine={false} axisLine={false} width={40} />
                      <Tooltip content={<ChartTip />} cursor={{ stroke: "#22d3ee", strokeOpacity: 0.3 }} />
                      <Area
                        type="monotone" dataKey="score" stroke="#22d3ee" strokeWidth={2.5}
                        fill="url(#scoreFill)"
                        dot={{ r: 3, fill: "#05060a", stroke: "#22d3ee", strokeWidth: 2 }}
                        activeDot={{ r: 5, fill: "#22d3ee" }}
                        style={{ filter: "drop-shadow(0 0 6px rgba(34,211,238,0.6))" }}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </motion.div>
            )}

            <div className="glass rounded-2xl mt-8 overflow-x-auto" data-testid="history-table">
              <table className="w-full font-mono text-sm border-collapse">
                <thead>
                  <tr className="text-left text-[var(--muted)]">
                    {["DATE", "PLATFORM", "SCORE", "STATUS", "USAGE AVOIDED", "PROMPT", ""].map((h) => (
                      <th key={h} className="p-4 font-normal text-[10px] tracking-[0.2em] border-b border-[var(--line)]">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {audits.map((a) => (
                    <tr key={a.id} className="border-b border-[var(--line)] hover:bg-neon-cyan/[0.04] transition-colors" data-testid={`history-row-${a.id}`}>
                      <td className="p-4 whitespace-nowrap text-[var(--muted)]">{fmt(a.created_at)}</td>
                      <td className="p-4 whitespace-nowrap text-white font-bold">{a.platform}</td>
                      <td className="p-4">
                        <span className="font-display font-bold text-lg" style={{ color: scoreColor(a.score), textShadow: `0 0 12px ${scoreColor(a.score)}66` }}>
                          {a.score}
                        </span>
                      </td>
                      <td className="p-4 whitespace-nowrap">
                        <span className="px-2 py-0.5 rounded-md text-[10px] font-bold tracking-[0.15em]"
                          style={{ color: scoreColor(a.score), border: `1px solid ${scoreColor(a.score)}66` }}>
                          {scoreBand(a.score)}
                        </span>
                      </td>
                      <td className="p-4 font-bold" style={{ color: a.usage_source ? "#22e58a" : "var(--muted)" }} data-testid={`history-usage-${a.id}`}>
                        {a.usage_source ? a.usage_display : "Not enough data to estimate"}
                      </td>
                      <td className="p-4 max-w-xs truncate text-[var(--muted)]">
                        {a.original_prompt ? a.original_prompt : <span className="italic">Original prompt not stored</span>}
                      </td>
                      <td className="p-4 whitespace-nowrap">
                        <HistoryRowActions audit={a} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
    </>
  );
}
